import { useState, type MouseEvent, type ReactNode } from 'react';
import { useController } from 'react-hook-form';
import { useObservableState } from 'observable-hooks';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faChartLine, faEdit } from '@fortawesome/free-solid-svg-icons';
import classNames from '../../../utils/classNames';
import { toStringOrEmpty } from '../../../utils/cast';
import Button, { ButtonContainer, LoadingButton, RoundedButton } from '../../form/Button';
import { NOTIFICATION_VARIANT, useNotifications } from '../../notifications/Notifications';
import { MarkdownEditor } from '../../reactForm/fields/MarkdownField/MarkdownEditor';
import { useSharedFormContext } from '../../reactForm/utils';
import { AlarmPreviewGraphPanel } from './AlarmPreviewGraph';
import { FloatingPanelTitle } from './FloatingPanelTitle';

type OpenPanel = 'alarm' | 'notes' | null;

function getRequiredPrivateNotesLabel(
    formController: ReturnType<typeof useSharedFormContext>['formController']
): string {
    const privateNotesLabel = formController.fields.getByName('privateNotes').label;
    if (!privateNotesLabel) {
        throw new Error('Missing translated label for privateNotes field.');
    }
    return privateNotesLabel;
}

interface FloatingPanelShellProps {
    title: string;
    actions: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
}

function FloatingPanelShell({
    title,
    actions,
    children,
    footer,
}: FloatingPanelShellProps): JSX.Element {
    return (
        <div
            className="flex max-h-[calc(100vh-8rem)] w-[32rem] max-w-[calc(100vw-7rem)] flex-col overflow-hidden rounded-lg bg-white shadow-lg"
            role="region"
            aria-label={title}
        >
            <div className="flex items-start justify-between border-b-0 border-neutral-300 px-5 pb-0 pt-5">
                <FloatingPanelTitle>{title}</FloatingPanelTitle>
                <div className="flex items-center gap-2">{actions}</div>
            </div>
            <div
                className={classNames(
                    'min-h-0 flex-1 overflow-y-auto px-5 pt-4',
                    !footer && 'pb-5'
                )}
            >
                {children}
            </div>
            {footer}
        </div>
    );
}

interface FloatingPanelToggleButtonProps {
    isActive: boolean;
    label: string;
    icon: IconDefinition;
    onClick: () => void;
}

function FloatingPanelToggleButton({
    isActive,
    label,
    icon,
    onClick,
}: FloatingPanelToggleButtonProps): JSX.Element {
    return (
        <RoundedButton
            type="button"
            variant="big"
            color="primary"
            className={classNames(
                'flex h-12 w-12 items-center justify-center px-0 shadow-lg',
                !isActive && 'brightness-95'
            )}
            aria-label={label}
            aria-pressed={isActive}
            title={label}
            onClick={onClick}
        >
            <FontAwesomeIcon icon={icon} />
        </RoundedButton>
    );
}

interface PrivateNotesFloatingPanelProps {
    onClose: () => void;
    canEditConfiguration: boolean;
}

export function PrivateNotesFloatingPanel({
    onClose,
    canEditConfiguration,
}: PrivateNotesFloatingPanelProps): JSX.Element {
    const { formController } = useSharedFormContext();
    const { addNotification } = useNotifications();
    const isCreateMode = !formController.id;
    const canSavePrivateNotes = !isCreateMode && canEditConfiguration;
    const canEditPrivateNotes = isCreateMode || canSavePrivateNotes;
    const mainSaveIsInProgress = useObservableState(formController.mainSaveIsInProgress$, false);
    const privateNotesSaveIsInProgress = useObservableState(
        formController.privateNotesSaveIsInProgress$,
        false
    );
    const privateNotesAreReadOnly = !canEditPrivateNotes;
    const privateNotesAreDisabled = privateNotesSaveIsInProgress || mainSaveIsInProgress;
    const privateNotesField = formController.fields.getByName('privateNotes');
    const privateNotesLabel = getRequiredPrivateNotesLabel(formController);
    const information = formController.information?.privateNotes;
    const { field, fieldState } = useController<Record<string, unknown>, 'privateNotes'>({
        name: 'privateNotes',
        defaultValue: toStringOrEmpty(privateNotesField.default),
    });
    function handleMinimize(_event: MouseEvent<HTMLButtonElement>) {
        field.onBlur();
        onClose();
    }

    function handleDiscardChanges(_event: MouseEvent<HTMLButtonElement>) {
        formController.rhfProps.resetField('privateNotes');
        field.onBlur();
    }

    async function handleSave(_event: MouseEvent<HTMLButtonElement>) {
        if (!canSavePrivateNotes || privateNotesSaveIsInProgress || mainSaveIsInProgress) {
            return;
        }

        field.onBlur();
        const privateNotes = toStringOrEmpty(field.value);
        const savedPrivateNotes = await formController.savePrivateNotesOnly(privateNotes);
        if (savedPrivateNotes === null) {
            return;
        }

        formController.commitPrivateNotesBaseline(savedPrivateNotes);
        addNotification({
            message: gettext('SAVED_SUCCESSFULLY'),
            variant: NOTIFICATION_VARIANT.SUCCESS,
        });
    }

    return (
        <FloatingPanelShell
            title={privateNotesLabel}
            actions={
                <button
                    type="button"
                    title={gettext('CLOSE')}
                    aria-label={gettext('CLOSE')}
                    onClick={handleMinimize}
                    className="text-sm text-gray-500 hover:text-gray-700"
                >
                    <FontAwesomeIcon icon={faAngleDown} />
                </button>
            }
            footer={
                canSavePrivateNotes ? (
                    <div className="px-5 pb-5 pt-4">
                        <div className="flex justify-end">
                            <ButtonContainer>
                                <Button
                                    variant="neutral"
                                    className="!rounded"
                                    onClick={handleDiscardChanges}
                                    type="button"
                                    disabled={privateNotesSaveIsInProgress || mainSaveIsInProgress}
                                >
                                    {gettext('DISCARD_CHANGES')}
                                </Button>
                                <LoadingButton
                                    variant="action"
                                    className="!rounded"
                                    aria-label={gettext('SAVE')}
                                    onClick={(event) => {
                                        void handleSave(event);
                                    }}
                                    type="button"
                                    isLoading={privateNotesSaveIsInProgress}
                                    disabled={
                                        !canSavePrivateNotes ||
                                        privateNotesSaveIsInProgress ||
                                        mainSaveIsInProgress
                                    }
                                />
                            </ButtonContainer>
                        </div>
                    </div>
                ) : null
            }
        >
            <div className="space-y-4">
                {information ? <p className="text-sm text-gray-600">{information}</p> : null}
                <MarkdownEditor
                    name={field.name}
                    value={toStringOrEmpty(field.value)}
                    onChange={field.onChange}
                    placeholder={privateNotesField.placeholder}
                    isReadOnly={privateNotesAreReadOnly}
                    isDisabled={privateNotesAreDisabled}
                />
                {fieldState.error?.message && (
                    <p className="text-xs text-red-600" role="alert">
                        {fieldState.error.message}
                    </p>
                )}
            </div>
        </FloatingPanelShell>
    );
}

interface MeasuringPointFloatingPanelsProps {
    showAlarmPreview: boolean;
    canEditConfiguration: boolean;
}

export function MeasuringPointFloatingPanels({
    showAlarmPreview,
    canEditConfiguration,
}: MeasuringPointFloatingPanelsProps): JSX.Element {
    const { formController } = useSharedFormContext();
    const [openPanel, setOpenPanel] = useState<OpenPanel>(showAlarmPreview ? 'alarm' : null);
    const privateNotesLabel = getRequiredPrivateNotesLabel(formController);
    const panelDefinitions = [
        {
            name: 'notes',
            label: privateNotesLabel,
            icon: faEdit,
            Element: PrivateNotesFloatingPanel,
            isVisible: true,
        },
        {
            name: 'alarm',
            label: gettext('PREVIEW_OF_ALARM_LIMIT'),
            icon: faChartLine,
            Element: AlarmPreviewGraphPanel,
            isVisible: showAlarmPreview,
        },
    ] as const;
    const visiblePanels = panelDefinitions.filter((panel) => panel.isVisible);

    return (
        <div className="z-modal fixed bottom-24 right-5 flex items-end gap-3">
            {visiblePanels.map(({ name, Element }) =>
                openPanel === name ? (
                    <Element
                        key={`panel-${name}`}
                        onClose={() => setOpenPanel(null)}
                        canEditConfiguration={canEditConfiguration}
                    />
                ) : null
            )}
            <div className="flex flex-col gap-3">
                {visiblePanels.map(({ name, label, icon }) => (
                    <FloatingPanelToggleButton
                        key={`toggle-${name}`}
                        isActive={openPanel === name}
                        label={label}
                        icon={icon}
                        onClick={() => {
                            setOpenPanel((currentPanel) => (currentPanel === name ? null : name));
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
